interface StripeSubscription {
  id: string;
  status: string;
  current_period_end: number;
  current_period_start: number;
  trial_end: number | null;
  trial_start: number | null;
  cancel_at: number | null;
  canceled_at: number | null;
  billing_cycle_anchor: number;
  items: {
    data: Array<{
      price: {
        unit_amount: number;
        currency: string;
        recurring: {
          interval: string;
        };
      };
    }>;
  };
  error?: string;
}

interface StripePlan {
  id: string;
  meter: null;
  active: boolean;
  amount: number;
  object: string;
  created: number;
  product: string;
  currency: string;
  interval: string;
  livemode: boolean;
  metadata: Record<string, any>;
  nickname: string | null;
  tiers_mode: string | null;
  usage_type: string;
  amount_decimal: string;
  billing_scheme: string;
  interval_count: number;
  aggregate_usage: string | null;
  transform_usage: string | null;
  trial_period_days: number | null;
}

export interface Subscription {
  id: string;
  npo_id: string;
  attrs: StripeSubscription;
}

interface SubscriptionResponse {
  success?: boolean;
  data: {
    id: string;
    plan: StripePlan;
    canceled_at: number | null;
    cancel_at: number | null;
    status: string;
    cancel_at_period_end: boolean;
  };
  message: string;
}

export const getNPOSubscription = async (userId: string, token: string): Promise<SubscriptionResponse | null> => {
  const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/subscriptions`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch subscription');
  }

  return response.json();
};

export const cancelSubscription = async (subscriptionId: string, token: string): Promise<string> => {
  const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/subscriptions/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to cancel subscription');
  }

  const { data } = await response.json();
  return data.url;
};

export const resumeSubscription = async (subscriptionId: string, token?: string): Promise<SubscriptionResponse> => {
  const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/hooks/stripe/subscription/resume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ subscription_id: subscriptionId })
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Error in resumeSubscription:', error);
    throw new Error(error.message || 'Failed to resume subscription');
  }

  return response.json();
};

export const isSubscribed = async (token: string) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/subscriptions/status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.data.is_subscribed;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

export const createCustomerPortalSession = async (token: string): Promise<string> => {
  const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/hooks/stripe/portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to create portal session');
  }

  const { data } = await response.json();
  return data.url;
};
